// Shared across every <cart-shipping-bar> instance on the page (notification
// popup + cart page + drawer can all be present at once), so the ~100-entry
// dataset is only ever fetched once.
let pincodeCoordinatesPromise = null;

function loadPincodeCoordinates(url) {
  if (!pincodeCoordinatesPromise) {
    pincodeCoordinatesPromise = fetch(url)
      .then((response) => response.json())
      .catch(() => ({}));
  }
  return pincodeCoordinatesPromise;
}

function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

function zoneFromDistanceKm(km) {
  if (km <= 5) return '0-5';
  if (km <= 10) return '5-10';
  return '10+';
}

class CartShippingBar extends HTMLElement {
  constructor() {
    super();

    // Shared with snippets/pincode-gate.liquid's add-to-cart pincode check —
    // same localStorage keys, so verifying in either place covers both.
    this.pincodeStorageKey = 'pincodeGateValue';
    this.pincodeVerifiedKey = 'pincodeGateVerified';
    this.zoneStorageKey = 'seeragam:delivery-zone';

    this.serviceablePincodes = (this.dataset.serviceablePincodes || '')
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    this.totalCents = parseInt(this.dataset.totalCents || '0', 10);
    this.threshold = null;

    this.coordinatesUrl = this.dataset.coordinatesUrl;
    this.depotLat = parseFloat(this.dataset.depotLat);
    this.depotLng = parseFloat(this.dataset.depotLng);

    this.pincodeCheck = this.querySelector('[data-pincode-check]');
    this.pincodeInput = this.querySelector('[data-pincode-input]');
    this.pincodeSubmit = this.querySelector('[data-pincode-submit]');
    this.pincodeMessage = this.querySelector('[data-pincode-message]');
    this.unserviceable = this.querySelector('[data-unserviceable]');
    this.unserviceableValue = this.querySelector('[data-unserviceable-value]');
    this.zonePicker = this.querySelector('[data-zone-picker]');
    this.zoneAutoNote = this.querySelector('[data-zone-auto-note]');
    this.progress = this.querySelector('[data-progress]');
    this.message = this.querySelector('.cart-shipping-bar__message');
    this.fill = this.querySelector('.cart-shipping-bar__fill');
    this.zoneButtons = Array.from(this.querySelectorAll('.cart-shipping-bar__zone-option'));

    this.pincodeSubmit.addEventListener('click', () => this.checkPincode());
    this.pincodeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.checkPincode();
      }
    });

    this.querySelectorAll('[data-pincode-change]').forEach((button) => {
      button.addEventListener('click', () => this.resetPincode());
    });

    this.zoneButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectZone(button.dataset.zone, true, false));
    });

    const savedZone = window.localStorage.getItem(this.zoneStorageKey);
    if (savedZone) this.selectZone(savedZone, false, false);

    this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshTotal());

    this.onPincodeChange = () => this.syncFromStorage();
    window.addEventListener('pincode-gate:change', this.onPincodeChange);

    this.syncFromStorage();
  }

  disconnectedCallback() {
    if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
    window.removeEventListener('pincode-gate:change', this.onPincodeChange);
  }

  isServiceable(value) {
    return this.serviceablePincodes.length === 0 || this.serviceablePincodes.indexOf(value) !== -1;
  }

  syncFromStorage() {
    const value = window.localStorage.getItem(this.pincodeStorageKey);
    const verified = window.localStorage.getItem(this.pincodeVerifiedKey) === '1';

    if (!value) {
      this.showPincodeCheck();
    } else if (verified) {
      this.showZonePicker();
    } else {
      this.showUnserviceable(value);
    }
  }

  showPincodeCheck() {
    this.pincodeCheck.hidden = false;
    this.unserviceable.hidden = true;
    this.zonePicker.hidden = true;
  }

  showUnserviceable(value) {
    this.pincodeCheck.hidden = true;
    this.unserviceable.hidden = false;
    this.zonePicker.hidden = true;
    this.unserviceableValue.textContent = value;
  }

  showZonePicker() {
    this.pincodeCheck.hidden = true;
    this.unserviceable.hidden = true;
    this.zonePicker.hidden = false;
    this.render();
  }

  checkPincode() {
    const value = (this.pincodeInput.value || '').trim();

    if (!/^[0-9]{6}$/.test(value)) {
      this.pincodeMessage.textContent = 'Please enter a valid 6-digit pincode.';
      return;
    }

    this.pincodeMessage.textContent = '';
    window.localStorage.setItem(this.pincodeStorageKey, value);

    const serviceable = this.isServiceable(value);
    if (serviceable) {
      window.localStorage.setItem(this.pincodeVerifiedKey, '1');
    } else {
      window.localStorage.removeItem(this.pincodeVerifiedKey);
    }

    window.dispatchEvent(new CustomEvent('pincode-gate:change', { detail: { value: value, verified: serviceable } }));

    if (serviceable) {
      this.showZonePicker();
      this.autoSelectZoneFromDistance(value);
    } else {
      this.showUnserviceable(value);
    }
  }

  // Looks up the pincode's coordinates in the bundled dataset and, if found,
  // computes real distance from the depot to auto-select the correct zone.
  // Falls back to leaving the zone buttons for manual selection if the
  // pincode isn't in the dataset or coordinates/depot data are missing.
  async autoSelectZoneFromDistance(pincode) {
    if (!this.coordinatesUrl || isNaN(this.depotLat) || isNaN(this.depotLng)) return;

    try {
      const coordinates = await loadPincodeCoordinates(this.coordinatesUrl);
      const entry = coordinates[pincode];
      if (!entry) return;

      const distanceKm = haversineDistanceKm(this.depotLat, this.depotLng, entry.lat, entry.lng);
      this.selectZone(zoneFromDistanceKm(distanceKm), true, true);
    } catch (e) {
      // leave manual zone buttons as the fallback
    }
  }

  resetPincode() {
    this.pincodeInput.value = window.localStorage.getItem(this.pincodeStorageKey) || '';
    this.pincodeMessage.textContent = '';
    this.showPincodeCheck();
    this.pincodeInput.focus();
  }

  // /cart/add.js returns the added line item, not the cart total, and the
  // shape differs again for quantity changes vs quick-add-bulk vs external
  // refreshes. Rather than special-casing every publisher, just re-fetch the
  // cart directly (same pattern already used in cart-notification.js).
  async refreshTotal() {
    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (typeof cart.total_price === 'number') {
        this.totalCents = cart.total_price;
        this.render();
      }
    } catch (e) {
      // keep the last known total if the fetch fails
    }
  }

  selectZone(zone, persist, auto) {
    const button = this.zoneButtons.find((btn) => btn.dataset.zone === zone);
    if (!button) return;

    this.zoneButtons.forEach((btn) => btn.classList.toggle('active', btn === button));
    this.threshold = parseInt(button.dataset.threshold, 10);

    if (this.zoneAutoNote) this.zoneAutoNote.hidden = !auto;

    if (persist) window.localStorage.setItem(this.zoneStorageKey, zone);

    this.render();
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (e) {
      return 'Rs. ' + Math.round(amount);
    }
  }

  render() {
    if (this.zonePicker.hidden || !this.threshold) {
      this.progress.hidden = true;
      return;
    }

    this.progress.hidden = false;
    const remaining = this.threshold - this.totalCents;
    const percent = Math.max(0, Math.min(100, (this.totalCents / this.threshold) * 100));
    this.fill.style.width = percent + '%';

    if (remaining <= 0) {
      this.progress.classList.add('cart-shipping-bar__progress--met');
      this.message.textContent = "You've unlocked FREE delivery!";
    } else {
      this.progress.classList.remove('cart-shipping-bar__progress--met');
      this.message.textContent = 'Add ' + this.formatMoney(remaining) + ' more for FREE delivery';
    }
  }
}

customElements.define('cart-shipping-bar', CartShippingBar);
